import { useState, useCallback, useEffect, useRef } from "react";
import { TextField } from "ui/components/TextField/TextField";
import { AddressAutocompleteProps, AddressAutocompleteSuggestion } from "./types";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

export default function AddressAutocomplete(props: AddressAutocompleteProps) {
  const { value, onChange, onSelect, label, placeholder, error, required } = props;

  const [suggestions, setSuggestions] = useState<AddressAutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isSelectingRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5`
      );
      const data = await res.json();

      setSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Error fetching address suggestions:", err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [value, fetchSuggestions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleSuggestionClick = (suggestion: AddressAutocompleteSuggestion) => {
    isSelectingRef.current = true;
    onChange(suggestion.place_name);
    setShowSuggestions(false);
    setSuggestions([]);
    onSelect(suggestion);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <TextField
        type="text"
        value={value}
        onChange={handleChange}
        label={label}
        placeholder={placeholder}
        error={error}
        required={required}
        endIcon=""
      />

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="cursor-pointer px-4 py-2 hover:bg-gray-100"
            >
              <p className="text-sm font-medium text-gray-900">{suggestion.text}</p>
              <p className="text-xs text-gray-500">{suggestion.place_name}</p>
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
        </div>
      )}
    </div>
  );
}
